import { extractPhone } from '#utils/lead/share-target';

/**
 * Extrae los datos de un lead de lo que llega de WhatsApp sin la API de Meta:
 * - Mensajes copiados (selecciona 2 o más mensajes → Copiar): "[8/10/26, 10:21 a. m.] +504 9999-8888: Hola"
 * - Chat exportado (Más → Exportar chat): "8/10/26, 10:21 a. m. - Juan Pérez: Hola"
 * - Contacto compartido (.vcf): FN / TEL / ORG
 * - Texto suelto: busca un teléfono dentro del texto.
 * Si el remitente no está guardado en la agenda, WhatsApp pone su número en lugar del nombre.
 */
export interface ParsedWhatsAppLead {
  phone: string | null;
  name: string | null;
  company: string | null;
  note: string | null;
  source: 'conversacion' | 'contacto' | 'texto';
}

/** Números y nombres propios del negocio: sus mensajes no son del cliente. */
const OWN_NUMBERS = ['50488187765', '50424451515'];
const OWN_SENDER_NAMES = ['tú', 'tu', 'you', 'yo', 'smart business'];

const COPIED_LINE = /^\[([^\]]{4,40})\]\s+([^:]{1,80}?):\s?(.*)$/;
const EXPORTED_LINE = /^(\d{1,2}\/\d{1,2}\/\d{2,4}),?\s+(\d{1,2}:\d{2}(?::\d{2})?\s?(?:[ap]\.?\s?m\.?)?)\s+-\s+([^:]{1,80}?):\s?(.*)$/i;
const MEDIA_PLACEHOLDER = /^<?(multimedia omitido|media omitted|archivo omitido|imagen omitida|video omitido|audio omitido|sticker omitido)>?$/i;

interface ChatMessage {
  sender: string;
  text: string;
}

const digitsOf = (value: string): string => value.replace(/\D/g, '');

function looksLikePhone(sender: string): boolean {
  const digits = digitsOf(sender);
  return /^[+\d\s().-]+$/.test(sender.trim()) && (digits.length === 8 || (digits.length >= 10 && digits.length <= 15));
}

function isOwnSender(sender: string): boolean {
  const normalized = sender.trim().toLowerCase();
  if (OWN_SENDER_NAMES.includes(normalized)) return true;
  const digits = digitsOf(sender);
  return digits.length >= 8 && OWN_NUMBERS.some(own => own.endsWith(digits) || digits.endsWith(own));
}

/** Convierte el texto en mensajes (las líneas sin encabezado continúan el mensaje anterior). */
function parseMessages(text: string): ChatMessage[] {
  const messages: ChatMessage[] = [];
  for (const rawLine of text.replace(/\r/g, '').split('\n')) {
    // WhatsApp mete marcas de dirección invisibles (U+200E/U+202A…) alrededor de números.
    const line = rawLine.replace(/[‎‏‪-‮⁦-⁩]/g, '').trim();
    if (!line) continue;
    const copied = COPIED_LINE.exec(line);
    const exported = copied ? null : EXPORTED_LINE.exec(line);
    if (copied) {
      messages.push({ sender: copied[2].trim(), text: copied[3].trim() });
    } else if (exported) {
      messages.push({ sender: exported[3].trim(), text: exported[4].trim() });
    } else if (messages.length > 0) {
      messages[messages.length - 1].text += '\n' + line;
    }
  }
  return messages;
}

export function parseWhatsAppText(text: string | null | undefined, fileName?: string | null): ParsedWhatsAppLead | null {
  if (!text?.trim()) return null;
  if (/BEGIN:VCARD/i.test(text)) return parseVCard(text);

  const messages = parseMessages(text);
  if (messages.length === 0) {
    return { phone: extractPhone(text), name: null, company: null, note: text.trim().slice(0, 600), source: 'texto' };
  }

  // El cliente es el primer remitente que no es el negocio (normalmente quien inició la conversación).
  const customer = messages.find(m => !isOwnSender(m.sender))?.sender ?? null;
  const customerMessages = messages
    .filter(m => m.sender === customer && m.text && !MEDIA_PLACEHOLDER.test(m.text))
    .slice(-6)
    .map(m => m.text);

  // "Chat de WhatsApp con Juan Pérez.txt" trae el nombre aunque los mensajes no lo tengan.
  const nameFromFile = fileName?.match(/(?:chat de whatsapp con|whatsapp chat with)\s+(.+?)(?:\.txt)?$/i)?.[1]?.trim() ?? null;
  const senderIsPhone = customer != null && looksLikePhone(customer);

  return {
    phone: senderIsPhone ? customer : extractPhone(customerMessages.join('\n')),
    name: senderIsPhone ? (nameFromFile && !looksLikePhone(nameFromFile) ? nameFromFile : null) : customer,
    company: null,
    note: customerMessages.length ? customerMessages.join('\n').slice(0, 600) : null,
    source: 'conversacion',
  };
}

/** Primer contacto de una tarjeta vCard (.vcf) compartida desde WhatsApp o la agenda. */
export function parseVCard(vcard: string): ParsedWhatsAppLead | null {
  const card = vcard.split(/END:VCARD/i)[0];
  // Las líneas que empiezan con espacio continúan la anterior (RFC 6350).
  const lines = card.replace(/\r\n[ \t]/g, '').replace(/\n[ \t]/g, '').split(/\r?\n/);
  let name: string | null = null;
  let company: string | null = null;
  const phones: { value: string; mobile: boolean }[] = [];
  for (const line of lines) {
    const separator = line.indexOf(':');
    if (separator < 0) continue;
    const key = line.slice(0, separator).toUpperCase();
    const value = line.slice(separator + 1).trim().replace(/\\,/g, ',').replace(/\\;/g, ';');
    if (key === 'FN' || key.startsWith('FN;')) name = value || name;
    else if ((key === 'N' || key.startsWith('N;')) && !name) name = value.split(';').filter(Boolean).reverse().join(' ').trim() || null;
    else if (key === 'ORG' || key.startsWith('ORG;')) company = value.split(';')[0] || null;
    else if (key.includes('TEL')) {
      // WhatsApp agrega "waid=50499998888" al teléfono del contacto.
      const waid = /WAID=(\d+)/i.exec(key)?.[1];
      phones.push({ value: waid ? '+' + waid : value, mobile: /CELL|WAID|MOBILE/i.test(key) });
    }
  }
  if (!name && phones.length === 0) return null;
  const phone = (phones.find(p => p.mobile) ?? phones[0])?.value ?? null;
  return { phone, name, company, note: null, source: 'contacto' };
}
